/** @typedef { import('src/models/appointment/_types').Appointment } Appointment */
/** @typedef { import('src/models/user/_types').User } User */
/** @typedef { import('src/models/visit/_types').Visit } Visit */
/** @typedef { import('src/types/utilityTypes').Timestamp } Timestamp */
import { getUnixTime, isBefore } from 'date-fns';

import { VisitStatus } from './_types';
/** @type {(visit: Visit) => string} */
export const getVisitTitle = (visit) => {
  if (visit.appointment) {
    return visit.appointment.serviceName;
  }
  return 'Open Gym Visit';
};

export const getReadableVisitStatus = (status) => {
  if (!status) {
    return '';
  }

  const mapping = {
    [VisitStatus.Reserved]: 'Reserved',
    [VisitStatus.Registered]: 'Confirmed',
    [VisitStatus.Completed]: 'Checked In',
    [VisitStatus.Cancelled]: 'Early cancel',
    [VisitStatus.NoShowed]: 'No show',
    [VisitStatus.LateCancelled]: 'Late cancel',
  };

  if (mapping[status]) {
    return mapping[status];
  }

  const [firstLetter, ...restOfStatus] = status;
  return `${firstLetter.toUpperCase()}${restOfStatus.join('')}`;
};

/** @type {(visit: Visit) => string} */
export const getVisitStatus = (visit) => {
  return getReadableVisitStatus(visit.status);
};

/** @type {(visit: Visit) => Timestamp} */
export const getVisitStartTime = (visit) => {
  if (visit.appointment && visit.completedAt) {
    return visit.completedAt;
  }
  if (visit.checkIn) {
    return visit.checkIn;
  }
  if (visit.createdAt) {
    return visit.createdAt;
  }
  throw new Error('Completed visit without checkin or completed time');
};

/** @type {(visit: Visit) => boolean} */
export const isCheckedIn = (visit) => {
  return visit.status === VisitStatus.Completed;
};

/** @type {(visit: Visit) => boolean} */
export const isNoShowed = (visit) => {
  return visit.status === VisitStatus.NoShowed;
};

/** @type {(visit: Visit) => boolean} */
export const isCanceled = (visit) => {
  return (
    visit.status === VisitStatus.Cancelled ||
    visit.status === VisitStatus.LateCancelled
  );
};

/** @type {(visit: Visit) => boolean} */
export const isClosedOut = (visit) => {
  return (
    visit.status === VisitStatus.Completed ||
    visit.status === VisitStatus.NoShowed ||
    visit.status === VisitStatus.LateCancelled ||
    visit.status === VisitStatus.Cancelled
  );
};

/** @type {(visit: Visit) => boolean} */
export const isExpired = (visit) => {
  if (!visit.appointment) {
    return false;
  }
  return isBefore(visit.appointment.endTime, getUnixTime(new Date()));
};

/** @type {(visit: Visit) => boolean} */
export const isGuest = (visit) => {
  return !!visit.chaperoneVisitId;
};

/** @type {(visit: Visit, appointment: Appointment) => User | undefined} */
export const getVisitChaperone = (visit, appointment) => {
  if (isGuest(visit)) {
    const chaperoneVisit = appointment.visits.find(
      (v) => v.id == visit.chaperoneVisitId
    );

    return chaperoneVisit?.user;
  }
};

/** @type {(visit: Visit, visits: Visit[]) => Visit[]} */
export const guestVisits = (visit, visits) => {
  return visits.filter((v) => visit.id == v.chaperoneVisitId);
};

export const isCheckIn = (visit) => {
  return visit.checkIn && !visit.appointmentId;
};
